<template>
  <div class="mobile-product-page">
    <MobileLayout>
      <MobileHeader :logoColor="'black'" />

      <transition name="fade">
        <div class="content-area">
          <!-- Hero 图（懒加载） -->
          <section class="m-hero">
            <img v-lazy="heroImageComputed" alt="Hero" />
            <div class="m-hero-gradient"></div>
            <div class="m-hero-caption">
              <div class="m-hero-title">{{ heroTitle }}</div>
              <div class="m-hero-desc">{{ heroDesc }}</div>
            </div>
          </section>

          <!-- Gallery 图片（懒加载） -->
          <section class="m-gallery">
            <div
              v-for="(item, idx) in renderedItems"
              :key="idx"
              class="m-gallery-item"
              :class="{ 'm-gallery-big': item.type === 'deco' }"
              @click="handleItemClick(item)"
            >
              <template v-if="item.type === 'product'">
                <img v-lazy="item.data.MainImage || defaultImage" :alt="item.data.ProductName" />
                <div class="m-gallery-info">
                  <div class="m-gallery-name">{{ item.data.ProductName }}</div>
                  <div class="m-gallery-category">{{ heroTitle }}</div>
                </div>
              </template>
              <template v-else>
                <img v-lazy="item.src" class="deco-img" alt="Decorative" />
              </template>
            </div>
          </section>
        </div>
      </transition>

      <div v-if="errorMessage" class="m-error">
        {{ errorMessage }}
      </div>

      <MobileFooter style="margin-top: 20rem;" />
      <div style="padding: 30rem; text-align: center; padding-bottom: 90rem;">
        <img src="@/assets/icon/logo_black.png" alt="logo" style="width: 15rem; height: 19rem;" />
      </div>
    </MobileLayout>
  </div>
</template>

<script>
import MobileLayout from '@/components/mobile/MobileLayout.vue'
import MobileHeader from '@/components/mobile/header.vue'
import MobileFooter from '@/components/mobile/footer.vue'
import { getCategoryDetail } from '@/api/product'
import { showMessage } from '@/config/message'

const bg = require('@/assets/mobile/product/TGT-Melissa-Marshall-27 2.png')

export default {
  name: 'MobileProductIndex',
  components: {
    MobileLayout,
    MobileHeader,
    MobileFooter,
  },
  data() {
    return {
      loading: true,
      errorMessage: '',
      categoryInfo: {},
      heroTitle: '',
      heroDesc: '',
      galleryItems: [],
      // 透明占位图，加载过程中不可见
      defaultImage: 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7',
      isMobile: true,
      isFetching: false,
    }
  },
  computed: {
    heroImageComputed() {
      return this.categoryInfo.DecorativeImage || bg
    },
    decorativeImageList() {
      const images = []
      for (let i = 2; i <= 15; i++) {
        const key = `DecorativeImage${i}`
        const url = this.categoryInfo[key]
        if (url && typeof url === 'string' && url.trim() !== '') {
          images.push(url)
        }
      }
      return images
    },
    renderedItems() {
      const products = this.galleryItems
      const decos = this.decorativeImageList
      const result = []
      let pIdx = 0
      let decoIdx = 0

      while (pIdx < products.length) {
        for (let i = 0; i < 6 && pIdx < products.length; i++) {
          result.push({ type: 'product', data: products[pIdx] })
          pIdx++
        }
        if (pIdx < products.length) {
          if (decos.length > 0) {
            const src = decos[decoIdx % decos.length]
            result.push({ type: 'deco', src })
            decoIdx++
          }
        }
      }
      return result
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.fetchCategoryDetail()
    })
  },
  methods: {
    getCategoryIdFromRoute() {
      const raw = this.$route.query.CategoryID
      if (raw === undefined || raw === null) return null
      const parsed = Number(raw)
      return isNaN(parsed) ? null : parsed
    },
    async fetchCategoryDetail() {
      if (this.isFetching) return
      this.isFetching = true

      let categoryId = this.getCategoryIdFromRoute()
      if (categoryId === null || categoryId === undefined) {
        categoryId = 999
      }

      this.loading = true
      this.errorMessage = ''

      const startTime = Date.now()

      try {
        const response = await getCategoryDetail(categoryId, 'mobile')
        const data = response.result || response
        console.log(data)
        this.categoryInfo = data
		
        this.heroTitle = data.CategoryName
        this.heroDesc = data.Description
		
        const products = (data.Products || []).map((p) => ({
          ProductID: p.ProductID,
          ProductName: p.ProductName,
          CategoryName: p.CategoryName || '',
          MainImage: p.MainImage || '',
          GalleryImage: p.GalleryImage || '',
          BigImage: p.BigImage || '',
        }))

        this.galleryItems = products
      } catch (error) {
        console.error('Failed to load category detail:', error)
        this.errorMessage = 'Failed to load products'
        showMessage('Failed to load products', 'error')
      } finally {
        const elapsed = Date.now() - startTime
        const minDisplay = 500
        const delay = Math.max(0, minDisplay - elapsed)
        setTimeout(() => {
          this.loading = false
          this.isFetching = false
          // 触发页面就绪事件（供 MobileLayout 使用）
          window.dispatchEvent(new Event('page-ready'))
		  
		  // ✅ 新增：滚动到顶部
		  this.scrollToTop()
        }, delay)
      }
    },
	scrollToTop() {
	  window.scrollTo({ top: 0, behavior: 'smooth' });
	  // 或强制立即滚动
	  document.documentElement.scrollTop = 0;
	  document.body.scrollTop = 0;
	},
    handleItemClick(item) {
      if (item.type === 'deco') return
      if (item.data && item.data.ProductID) {
        this.$router.push(`/m/product/${item.data.ProductID}`)
      } else {
        this.$router.push('/m/product/preview')
      }
    },
  },
  watch: {
    '$route.query.CategoryID'() {
      if (this.isFetching) return
      this.fetchCategoryDetail()
    },
  },
}
</script>

<style scoped>
/* 根容器 */
.mobile-product-page {
  /* 无额外样式 */
}

/* 过渡动画 */
.fade-enter-active {
  transition: opacity 0.6s ease;
}
.fade-enter-from {
  opacity: 0;
}
.fade-leave-active {
  transition: opacity 0.3s ease;
}
.fade-leave-to {
  opacity: 0;
}

/* 加载占位已移除（原注释删除） */

/* ===== 其他原有样式 ===== */
.m-hero {
  position: relative;
  font-family: 'Apercu-Medium', sans-serif;
  margin-bottom: 2px;
}
.m-hero img {
  width: 100%;
  height: 509rem;
  object-fit: cover;
}
.m-hero-gradient {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 34%;
  background: linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.55) 100%);
}
.m-hero-caption {
  position: absolute;
  left: 16rem;
  right: 16rem;
  bottom: 40rem;
  display: flex;
  flex-direction: column;
  gap: 6rem;
  color: #fff;
}
.m-hero-title {
  font-size: 13rem;
  font-weight: 600;
}
.m-hero-desc {
  font-size: 10rem;
  max-width: 100%;
  text-shadow: 0 2rem 14rem rgba(0, 0, 0, 0.5);
}
.m-gallery {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2px;
  margin-bottom: 60rem;
}
.m-gallery-item {
  display: flex;
  flex-direction: column;
  width: 100%;
  background: #fff;
  cursor: pointer;
}
.m-gallery-item img {
  width: 100%;
  height: 203rem;
  object-fit: cover;
}
.m-gallery-item.m-gallery-big {
  grid-column: span 2;
  width: 100%;
}
.m-gallery-item.m-gallery-big img {
  height: 509rem;
}
.m-gallery-info {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 7rem 0;
  font-family: 'Apercu-Medium', sans-serif;
  height: 36rem;
}
.m-gallery-name {
  font-size: 8rem;
  font-weight: 600;
  color: #000;
  margin-bottom: 2rem;
  margin-left: 12rem;
}
.m-gallery-category {
  font-size: 6rem;
  color: #999;
  font-weight: 400;
  margin-left: 12rem;
}
.m-error {
  text-align: center;
  padding: 80rem;
  font-size: 14rem;
  color: #666;
}

/* ===== 懒加载图片过渡：加载中透明，加载后淡入 ===== */
img[lazy="loading"] {
  opacity: 0;
}
img[lazy="loaded"] {
  opacity: 1;
  transition: opacity 0.4s ease;
}
</style>